/**
 * Fort-Pierre (GDD section 10): the map of the GDD's preview (fort-data.ts). A plateau ringed with moats, entered by
 * a wooden drawbridge under the portcullis of the gate tower, which bears the oil cauldron; two crenellated walls,
 * the keep with its flag and, under it, the powder magazine (barrels of black powder); a well in the courtyard, a
 * secret tunnel from the magazine to the village on the east bank; wooden houses and dead trees outside.
 * Added here for the game: the cauldron and the barrels redrawn for their events (maps/events.ts), and a tunnel
 * (8 cells high in the preview) widened to 39 so that a unit walks and jumps in it, closed by trapdoors. The seed changes nothing here.
 */
import { Physique, decodeGrid } from '@invaders/sim';
import { plainMap, type Box, type MapState } from '../state';
import { type Gen, rect, sea, settle } from './common';
import { FORT_PIERRE_GRID } from './fort-data';

const { EMPTY, ROCK, WOOD, OIL, BLACK_POWDER } = Physique;

/** The oil cauldron on the gate tower's merlons: a bowl of rock, walls and bottom 2 cells thick. */
export const FORT_CAULDRON: Box = { x0: 348, y0: 165, x1: 365, y1: 172 };
/** Where its oil burns: the portcullis and the drawbridge, under the gate tower. */
export const FORT_GATE: Box = { x0: 296, y0: 250, x1: 385, y1: 300 };
/** The powder magazine: the cellar under the keep. */
export const FORT_MAGAZINE: Box = { x0: 478, y0: 304, x1: 545, y1: 331 };

/** The portcullis' bars (wood, some of their dark cells came out of the preview as rock). */
const PORTCULLIS: Box = { x0: 342, y0: 258, x1: 352, y1: 291 };
/**
 * The secret tunnel: 39 rows (a unit is 16, it can jump in it; 26 before, half as high again by the author's choice),
 * from just under the moats (4 rows of rock) down to the row over the sea level (sea() fills every empty cell under it).
 */
const TUNNEL_TOP = 361;
const TUNNEL_BOTTOM = 399;
/**
 * Its shafts, 34 wide (24 before): from a trapdoor in the courtyard, through the cellar (between the barrels and the
 * well); up into the village's first house, between its posts.
 */
const CELLAR_SHAFT = 526;
const VILLAGE_SHAFT = 767;
const SHAFT_W = 34;
/** The well: its water rests on a plug of rock over the tunnel. */
const WELL_X0 = 562;
const WELL_X1 = 573;

/**
 * True while the cauldron's bowl is whole (its walls and bottom all rock): `box` is FORT_CAULDRON. A shot that
 * breaks a cell of it tips it over (maps/events.ts).
 */
export function cauldronIntact(data: Uint8Array, w: number, box: Box): boolean {
  const { x0, y0, x1, y1 } = box;
  const rock = (xa: number, ya: number, xb: number, yb: number) => {
    for (let y = ya; y <= yb; y++) for (let x = xa; x <= xb; x++) if (data[y * w + x] !== ROCK) return false;
    return true;
  };
  return rock(x0, y0, x0 + 1, y1) && rock(x1 - 1, y0, x1, y1) && rock(x0, y1 - 1, x1, y1);
}

/** A barrel of black powder, 15 wide and 16 high, standing on `floorY`: wooden staves 2 thick around the powder. */
function barrel(g: Gen, x0: number, floorY: number): void {
  rect(g, x0, floorY - 16, x0 + 14, floorY - 1, WOOD);
  rect(g, x0 + 2, floorY - 14, x0 + 12, floorY - 3, BLACK_POWDER);
}

export function drawFort(g: Gen): MapState {
  g.data.set(decodeGrid(FORT_PIERRE_GRID, g.w * g.h));

  const c = FORT_CAULDRON;
  rect(g, c.x0, c.y0, c.x1, c.y1, EMPTY);
  rect(g, c.x0, c.y0, c.x0 + 1, c.y1, ROCK);
  rect(g, c.x1 - 1, c.y0, c.x1, c.y1, ROCK);
  rect(g, c.x0, c.y1 - 1, c.x1, c.y1, ROCK);
  rect(g, c.x0 + 2, c.y0 + 1, c.x1 - 2, c.y1 - 2, OIL);
  rect(g, PORTCULLIS.x0, PORTCULLIS.y0, PORTCULLIS.x1, PORTCULLIS.y1, WOOD, ROCK);

  // The magazine: three barrels on a floor of rock.
  const m = FORT_MAGAZINE;
  rect(g, m.x0, m.y0, m.x1, m.y1, EMPTY);
  rect(g, m.x0, m.y1 + 1, m.x1, m.y1 + 2, ROCK);
  for (const x of [480, 495, 510]) barrel(g, x, m.y1 + 1);

  // The secret tunnel and its shafts, each closed at the top by a wooden trapdoor.
  rect(g, CELLAR_SHAFT, 300, CELLAR_SHAFT + SHAFT_W - 1, TUNNEL_TOP, EMPTY);
  rect(g, CELLAR_SHAFT, 300, CELLAR_SHAFT + SHAFT_W - 1, 301, WOOD);
  rect(g, CELLAR_SHAFT, TUNNEL_TOP, VILLAGE_SHAFT + SHAFT_W - 1, TUNNEL_BOTTOM, EMPTY);
  rect(g, VILLAGE_SHAFT, 301, VILLAGE_SHAFT + SHAFT_W - 1, TUNNEL_TOP, EMPTY);
  rect(g, VILLAGE_SHAFT, 301, VILLAGE_SHAFT + SHAFT_W - 1, 302, WOOD);
  rect(g, WELL_X0, TUNNEL_TOP - 2, WELL_X1, TUNNEL_TOP - 1, ROCK);

  sea(g);
  settle(g);
  return { ...plainMap('fort'), cauldron: { ...FORT_CAULDRON }, gate: { ...FORT_GATE }, magazine: { ...FORT_MAGAZINE } };
}
